import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CircleIcon from '@mui/icons-material/Circle';
import { Helmet } from 'react-helmet-async';

import { SITE_URL } from '../lib/seo';
import { tArray } from '../lib/i18nSafe';
import StreamingDemo from './StreamingDemo';
import CodeSample from './CodeSample';
import GetInTouchSection from './getintouch';
import FestivalBanner from './FestivalBanner';
import FlagshipSection from './FlagshipSection';
import VideoSection from './VideoSection';
import NewsInsights from './NewsInsights';
import skylineAccent from '../assets/urbanai-skyline-accent.png';
import skylineBg from '../assets/urbanai-flagship-bg.png';

// ─────────────────────────────────────────────────────────────
// Home — UAE sovereign-AI positioning, 2026 editorial aesthetic.
// ─────────────────────────────────────────────────────────────

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } }
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } }
};

function Stat({ value, label }) {
  return (
    <Stack spacing={0.5}>
      <Typography
        sx={{
          fontFamily: 'var(--font-serif)',
          fontSize: { xs: '1.7rem', md: '2.2rem' },
          lineHeight: 1,
          letterSpacing: '-0.02em'
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.7rem',
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.12em'
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

export default function Home() {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();

  const MotionBox = motion(Box);

  const stats = tArray(t, 'home.stats');
  const principles = tArray(t, 'home.principles.items');
  const partners = tArray(t, 'home.trustedBy.items');
  const thesisBullets = tArray(t, 'home.thesis.bullets');
  const pillars = tArray(t, 'home.platform.items');
  const sectors = tArray(t, 'home.sectors.items');

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Al-Amen Technology',
    url: SITE_URL,
    logo: `${SITE_URL}/logo512.png`,
    sameAs: [
      'https://www.linkedin.com/company/al-amen-technology',
      'https://x.com/al_amen25895'
    ]
  };

  return (
    <Box>
      <Helmet>
        <title>Al-Amen Technology — Arabic-First AI for Smarter Cities and Institutions</title>
        <meta
          name="description"
          content="Al-Amen Technology develops AI applications that help organizations recover lost assets, understand operational patterns and make better decisions through urban intelligence."
        />
        <link rel="canonical" href={SITE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Al-Amen Technology — Arabic-First AI for Smarter Cities and Institutions" />
        <meta
          property="og:description"
          content="AI-powered lost asset recovery and urban intelligence for venues and institutions, plus wider AI application development."
        />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
      </Helmet>

      <FestivalBanner />

      {/* ─────────── HERO ─────────── */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
          overflow: 'hidden',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${skylineBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'top center',
            maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 65%)',
            WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,0.5) 0%, transparent 65%)',
            opacity: 0.4,
            pointerEvents: 'none'
          }}
        />
        <Box
          className="alamen-grid-bg"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            maskImage:
              'radial-gradient(900px 520px at 70% 10%, rgba(0,0,0,0.9), transparent 60%)',
            WebkitMaskImage:
              'radial-gradient(900px 520px at 70% 10%, rgba(0,0,0,0.9), transparent 60%)',
            opacity: 0.55,
            pointerEvents: 'none'
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          <MotionBox variants={stagger} initial={reduceMotion ? false : 'hidden'} animate={reduceMotion ? false : 'show'}>
            <MotionBox variants={fadeUp}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
                <CircleIcon sx={{ fontSize: 10, color: 'success.main' }} />
                <Typography
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    color: 'text.secondary'
                  }}
                >
                  {t('home.hero.eyebrow')}
                </Typography>
              </Stack>
            </MotionBox>

            <Grid container spacing={{ xs: 5, md: 8 }} alignItems="flex-start">
              <Grid item xs={12} md={7}>
                <MotionBox variants={fadeUp}>
                  <Typography
                    component="h1"
                    sx={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: { xs: '2.8rem', sm: '4rem', md: '5.2rem' },
                      lineHeight: 1.02,
                      letterSpacing: '-0.025em',
                      fontWeight: 400,
                      color: 'text.primary',
                      mb: 3
                    }}
                  >
                    {t('home.hero.titleLead')}
                    <Box component="em" sx={{ fontStyle: 'italic', color: 'primary.main' }}>
                      {t('home.hero.titleAccent')}
                    </Box>
                    {t('home.hero.titleTail')}
                  </Typography>
                </MotionBox>

                <MotionBox variants={fadeUp}>
                  <Typography
                    sx={{
                      fontSize: { xs: '1.05rem', md: '1.18rem' },
                      color: 'text.secondary',
                      maxWidth: 640,
                      lineHeight: 1.55,
                      mb: 4
                    }}
                  >
                    {t('home.hero.lede')}
                  </Typography>
                </MotionBox>

                {Array.isArray(stats) && stats.length ? (
                  <MotionBox variants={fadeUp}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      divider={<Divider orientation="vertical" flexItem />}
                      spacing={{ xs: 2, sm: 3 }}
                      sx={{ mt: { xs: 5, md: 6 } }}
                    >
                      {stats.map((s) => (
                        <Stat key={s.label} value={s.value} label={s.label} />
                      ))}
                    </Stack>
                  </MotionBox>
                ) : null}
              </Grid>

              <Grid item xs={12} md={5}>
                <MotionBox variants={fadeUp}>
                  <StreamingDemo />
                </MotionBox>
                <MotionBox variants={fadeUp}>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.72rem',
                      color: 'text.secondary',
                      mt: 1.25,
                      textAlign: { xs: 'center', md: 'right' }
                    }}
                  >
                    {t('home.hero.demoNote')}
                  </Typography>
                </MotionBox>
              </Grid>
            </Grid>
          </MotionBox>
        </Container>
      </Box>

      <FlagshipSection />

      {/* ─────────── TRUSTED BY / SECTOR STRIP ─────────── */}
      {Array.isArray(partners) && partners.length ? (
        <Box component="section" sx={{ py: { xs: 4, md: 5 }, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Container maxWidth="xl">
            <Typography
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                color: 'text.secondary',
                textAlign: 'center',
                mb: 3
              }}
            >
              {t('home.trustedBy.title')}
            </Typography>
            <Grid container spacing={3} justifyContent="center" alignItems="center">
              {partners.map((p) => (
                <Grid key={p} item xs={6} sm={4} md={2}>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: { xs: '1.2rem', md: '1.4rem' },
                      textAlign: 'center',
                      color: 'text.secondary',
                      opacity: 0.9,
                      letterSpacing: '-0.01em'
                    }}
                  >
                    {p}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      ) : null}

      {/* ─────────── THESIS ─────────── */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 4, md: 8 }}>
            <Grid item xs={12} md={5}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'text.secondary',
                  mb: 1.5
                }}
              >
                {t('home.thesis.eyebrow')}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: { xs: '2.2rem', md: '3rem' },
                  lineHeight: 1.04,
                  letterSpacing: '-0.02em',
                  mb: 3
                }}
              >
                {t('home.thesis.title')}
              </Typography>
              <Box
                component="img"
                src={skylineAccent}
                alt=""
                loading="lazy"
                sx={{
                  display: { xs: 'none', md: 'block' },
                  width: '100%',
                  maxWidth: 320,
                  aspectRatio: '240 / 225',
                  objectFit: 'cover',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography sx={{ color: 'text.secondary', mb: 3, fontSize: '1.08rem', lineHeight: 1.6 }}>
                {t('home.thesis.body')}
              </Typography>
              <Stack spacing={1.25}>
                {thesisBullets.map((b) => (
                  <Stack key={b} direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        mt: '0.55em',
                        flexShrink: 0
                      }}
                    />
                    <Typography sx={{ color: 'text.primary' }}>{b}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─────────── PLATFORM PILLARS ─────────── */}
      <Box
        component="section"
        sx={{
          py: { xs: 7, md: 10 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 3, md: 6 }}>
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'text.secondary',
                  mb: 1.5
                }}
              >
                {t('home.platform.eyebrow')}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  lineHeight: 1.06,
                  letterSpacing: '-0.02em',
                  mb: 2
                }}
              >
                {t('home.platform.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', maxWidth: 420, mb: 3 }}>
                {t('home.platform.intro')}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  component={RouterLink}
                  to="/platform"
                  variant="text"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ color: 'text.primary' }}
                >
                  {t('home.platform.cta')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/architecture"
                  variant="text"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ color: 'text.primary' }}
                >
                  {t('nav.architecture')}
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12} md={8}>
              <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                {pillars.map((p) => (
                  <Box
                    key={p.id}
                    component={RouterLink}
                    to="/platform"
                    sx={{
                      display: 'block',
                      textDecoration: 'none',
                      color: 'inherit',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: { xs: 3, md: 3.5 },
                      transition: 'background 180ms ease',
                      '&:hover': {
                        bgcolor: 'background.default',
                        '& .alamen-pillar-title': { color: 'primary.main' },
                        '& .alamen-pillar-arrow': { transform: 'translateX(4px)' }
                      }
                    }}
                  >
                    <Grid container spacing={2} alignItems="baseline">
                      <Grid item xs={2} sm={1}>
                        <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', color: 'text.secondary' }}>
                          {p.num}
                        </Typography>
                      </Grid>
                      <Grid item xs={10} sm={4}>
                        <Typography
                          className="alamen-pillar-title"
                          sx={{
                            fontFamily: 'var(--font-serif)',
                            fontSize: { xs: '1.4rem', md: '1.7rem' },
                            lineHeight: 1.1,
                            letterSpacing: '-0.015em',
                            transition: 'color 180ms ease'
                          }}
                        >
                          {p.title}
                        </Typography>
                        <Chip
                          label={p.product}
                          size="small"
                          sx={{
                            mt: 0.75,
                            fontFamily: 'var(--font-mono)',
                            bgcolor: 'primary.main',
                            color: 'background.default'
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ color: 'text.secondary', mb: 1 }}>{p.body}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={1} sx={{ textAlign: 'right' }}>
                        <ArrowForwardRoundedIcon
                          className="alamen-pillar-arrow"
                          fontSize="small"
                          sx={{
                            color: 'text.secondary',
                            transition: 'transform 180ms ease, color 180ms ease'
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─────────── SECTORS ─────────── */}
      <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
        <Container maxWidth="xl">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
            justifyContent="space-between"
            sx={{ mb: 4 }}
            spacing={2}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'text.secondary',
                  mb: 1
                }}
              >
                {t('home.sectors.eyebrow')}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: { xs: '2rem', md: '2.8rem' },
                  lineHeight: 1.06,
                  letterSpacing: '-0.02em'
                }}
              >
                {t('home.sectors.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mt: 1.5, maxWidth: 640 }}>
                {t('home.sectors.intro')}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to="/sectors"
              variant="text"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{ color: 'text.primary' }}
            >
              {t('home.sectors.cta')}
            </Button>
          </Stack>

          <Grid container spacing={3}>
            {sectors.map((s) => (
              <Grid key={s.id} item xs={12} sm={6} md={4}>
                <Paper
                  component={RouterLink}
                  to={`/sectors#${s.id}`}
                  variant="outlined"
                  sx={{
                    display: 'block',
                    textDecoration: 'none',
                    color: 'inherit',
                    p: { xs: 3, md: 3.5 },
                    borderRadius: 3,
                    height: '100%',
                    bgcolor: 'background.paper',
                    transition: 'transform 200ms ease, border-color 200ms ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: 'primary.main'
                    }
                  }}
                >
                  <Typography
                    sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'text.secondary', mb: 1.5 }}
                  >
                    {s.num}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'var(--font-serif)',
                      fontSize: '1.7rem',
                      lineHeight: 1.1,
                      letterSpacing: '-0.015em',
                      mb: 1
                    }}
                  >
                    {s.title}
                  </Typography>
                  <Typography sx={{ color: 'text.secondary', mb: 2 }}>{s.body}</Typography>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {(s.tags || []).map((tag) => (
                      <Chip key={tag} label={tag} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <VideoSection />

      {/* ─────────── DEVELOPER SURFACE ─────────── */}
      <Box
        component="section"
        sx={{
          py: { xs: 7, md: 10 },
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  color: 'text.secondary',
                  mb: 1.5
                }}
              >
                {t('home.code.eyebrow')}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: 'var(--font-serif)',
                  fontSize: { xs: '2rem', md: '2.6rem' },
                  lineHeight: 1.08,
                  letterSpacing: '-0.02em',
                  mb: 2
                }}
              >
                {t('home.code.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 3, maxWidth: 520 }}>
                {t('home.code.body')}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                {tArray(t, 'home.code.features').map((f) => (
                  <Chip key={f} label={f} variant="outlined" size="small" />
                ))}
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                <Button
                  component={RouterLink}
                  to="/models"
                  variant="text"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ color: 'text.primary' }}
                >
                  {t('nav.models')}
                </Button>
                <Button
                  component={RouterLink}
                  to="/changelog"
                  variant="text"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ color: 'text.primary' }}
                >
                  {t('nav.changelog')}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <CodeSample />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ─────────── PRINCIPLES ─────────── */}
      {Array.isArray(principles) && principles.length ? (
        <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
          <Container maxWidth="xl">
            <Grid container spacing={{ xs: 3, md: 6 }}>
              <Grid item xs={12} md={4}>
                <Typography
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.72rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.14em',
                    color: 'text.secondary',
                    mb: 1.5
                  }}
                >
                  {t('home.principles.eyebrow')}
                </Typography>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: 'var(--font-serif)',
                    fontSize: { xs: '2rem', md: '2.6rem' },
                    lineHeight: 1.08,
                    letterSpacing: '-0.02em'
                  }}
                >
                  {t('home.principles.title')}
                </Typography>
                <Button
                  component={RouterLink}
                  to="/trust"
                  variant="text"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{ mt: 2, color: 'text.primary' }}
                >
                  {t('home.principles.cta')}
                </Button>
              </Grid>
              <Grid item xs={12} md={8}>
                <Grid container spacing={3}>
                  {principles.map((p, idx) => (
                    <Grid key={p.title} item xs={12} sm={6}>
                      <Stack spacing={1}>
                        <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'text.secondary' }}>
                          0{idx + 1}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'var(--font-serif)',
                            fontSize: '1.45rem',
                            letterSpacing: '-0.015em',
                            lineHeight: 1.18
                          }}
                        >
                          {p.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {p.body}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>
      ) : null}

      <NewsInsights />

      <GetInTouchSection />
    </Box>
  );
}
